import React, { useMemo } from "react";
import { Pencil, Trash2 } from "lucide-react";
import StatusPill from "../../../Common/StatusPill/StatusPill";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU } from "../../../../lib/permissions";

const EMPTY = "—";
const LOW_STOCK_THRESHOLD = 10;

const ALL_COLUMNS = [
  { key: "barcode", label: "Barcode" },
  { key: "name", label: "Product", className: "plx-col-name" },
  { key: "variant", label: "Variant" },
  { key: "stock", label: "In stock" },
  { key: "regular_price", label: "Regular Price", className: "text-end" },
  { key: "sale_price", label: "Sale Price", className: "text-end" },
  { key: "type", label: "Type" },
  { key: "b2b", label: "B2B" },
  { key: "status", label: "Status" },
  { key: "created_by", label: "Created By" },
  { key: "actions", label: "Control", className: "text-end" },
];

const fmtMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "0";
  return num.toFixed(2);
};

const stockLabel = (qty) => {
  const n = Number(qty) || 0;
  if (n === 0) return { text: "Out of stock", tone: "danger" };
  if (n <= LOW_STOCK_THRESHOLD) return { text: "Low stock", tone: "danger" };
  return { text: "In stock", tone: "success" };
};

const StatusCell = ({ value }) => {
  if (value === 1) return <StatusPill tone="success">Active</StatusPill>;
  if (value === 2) return <StatusPill tone="danger">Inactive</StatusPill>;
  return <span className="plx-muted">{EMPTY}</span>;
};

const resolveLabel = (raw, lookup) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const key = String(raw).trim();
  if (!key) return "";
  return lookup?.get(key) || key;
};

const VariantCell = ({ item, sizeById, colorById }) => {
  const size = resolveLabel(item.size, sizeById);
  const color = resolveLabel(item.color, colorById);
  if (!size && !color) return <span className="plx-muted">{EMPTY}</span>;
  return (
    <div className="plx-variant">
      {size && (
        <StatusPill tone="neutral" icon={null}>
          {size}
        </StatusPill>
      )}
      {color && (
        <StatusPill tone="neutral" icon={null}>
          {color}
        </StatusPill>
      )}
    </div>
  );
};

const NameCell = ({ item }) => (
  <div className="plx-name">
    {item.image ? (
      <a
        href={item.image}
        target="_blank"
        rel="noopener noreferrer"
        className="plx-thumb-link"
        aria-label={`View ${item.name || "product"} image`}
      >
        <img
          src={item.image}
          className="plx-thumb"
          alt={item.name || "product"}
          loading="lazy"
        />
      </a>
    ) : (
      <div className="plx-thumb plx-thumb--empty" aria-hidden="true" />
    )}
    <div className="plx-name-text">
      <div className="plx-name-primary" title={item.name}>
        {item.name || EMPTY}
      </div>
      {item.ar_name && (
        <div className="plx-name-secondary" dir="rtl">
          {item.ar_name}
        </div>
      )}
    </div>
  </div>
);

const StockCell = ({ quantity }) => {
  const n = Number(quantity) || 0;
  const status = stockLabel(n);
  return (
    <div className="plx-stock">
      <div className="plx-stock__primary">{n} in stock</div>
      <div className={`plx-stock__secondary plx-stock__secondary--${status.tone}`}>
        {status.text}
      </div>
    </div>
  );
};

const RowActions = ({ item, onEdit, onDelete, canDelete }) => (
  <div className="plx-actions">
    <button
      type="button"
      className="plx-btn-details"
      onClick={() => onEdit(item)}
    >
      Details
    </button>
    {canDelete && (
      <button
        type="button"
        className="plx-iconbtn plx-iconbtn--danger"
        onClick={() => onDelete(item)}
        aria-label={`Delete ${item.name || "product"}`}
        title="Delete"
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    )}
    <button
      type="button"
      className="plx-iconbtn"
      onClick={() => onEdit(item)}
      aria-label={`Edit ${item.name || "product"}`}
      title="Edit"
    >
      <Pencil size={16} aria-hidden="true" />
    </button>
  </div>
);

const ProductRow = ({
  item,
  onEdit,
  onDelete,
  canDelete,
  sizeById,
  colorById,
  showRegularPrice,
  showSalePrice,
}) => (
  <tr>
    <td className="plx-mono">{item.barcode || EMPTY}</td>
    <td className="plx-col-name">
      <NameCell item={item} />
    </td>
    <td>
      <VariantCell item={item} sizeById={sizeById} colorById={colorById} />
    </td>
    <td>
      <StockCell quantity={item.quantity} />
    </td>
    {showRegularPrice && (
      <td className="text-end plx-num">{fmtMoney(item.regular_price)}</td>
    )}
    {showSalePrice && (
      <td className="text-end plx-num">{fmtMoney(item.sale_price)}</td>
    )}
    <td>
      {item.type?.name ? (
        <StatusPill tone="accent" icon={null}>
          {item.type.name}
        </StatusPill>
      ) : (
        <span className="plx-muted">{EMPTY}</span>
      )}
    </td>
    <td>
      {item.is_b2b === 1 ? (
        <StatusPill tone="info" icon={null}>B2B</StatusPill>
      ) : (
        <span className="plx-muted">{EMPTY}</span>
      )}
    </td>
    <td>
      <StatusCell value={item.status} />
    </td>
    <td>{item.created_by || EMPTY}</td>
    <td className="text-end">
      <RowActions
        item={item}
        onEdit={onEdit}
        onDelete={onDelete}
        canDelete={canDelete}
      />
    </td>
  </tr>
);

const SkeletonRow = ({ cols }) => (
  <tr className="plx-skeleton-row">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i}>
        <span className="plx-skeleton-bar" />
      </td>
    ))}
  </tr>
);

const EmptyState = ({ cols }) => (
  <tr>
    <td colSpan={cols} className="plx-empty">
      <div className="plx-empty__inner">
        <i className="fa fa-box-open plx-empty__icon" aria-hidden="true" />
        <div className="plx-empty__title">No products found</div>
        <div className="plx-empty__hint">
          Try adjusting your filters or search keyword.
        </div>
      </div>
    </td>
  </tr>
);

const ProductTable = ({
  items,
  loading,
  onEdit,
  onDelete,
  canDelete,
  sizeById,
  colorById,
}) => {
  const { hasMenuAccess } = usePermissions();
  const showRegularPrice = hasMenuAccess(MENU.PRODUCT_REGULAR_PRICE);
  const showSalePrice = hasMenuAccess(MENU.PRODUCT_SALE_PRICE);

  const columns = useMemo(() => {
    const drop = new Set();
    if (!showRegularPrice) drop.add("regular_price");
    if (!showSalePrice) drop.add("sale_price");
    return ALL_COLUMNS.filter((c) => !drop.has(c.key));
  }, [showRegularPrice, showSalePrice]);

  return (
    <div className="plx-table-scroll">
      <table className="plx-table" aria-busy={loading} aria-label="Products">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.className || ""}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && items.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i} cols={columns.length} />
            ))
          ) : items.length === 0 ? (
            <EmptyState cols={columns.length} />
          ) : (
            items.map((item) => (
              <ProductRow
                key={item.id}
                item={item}
                onEdit={onEdit}
                onDelete={onDelete}
                canDelete={canDelete}
                sizeById={sizeById}
                colorById={colorById}
                showRegularPrice={showRegularPrice}
                showSalePrice={showSalePrice}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
